"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Button, Input, Panel } from "@/components/ui";
import { useToast } from "@/components/toast";
import { api } from "@/lib/api";
import { startPageProgress } from "@/lib/progress";
import { useI18n } from "@/components/shell";

export default function RegisterPage() {
  const router = useRouter();
  const { locale, tt } = useI18n();
  const { toastError, toastOk } = useToast();
  const [requireInvite, setRequireInvite] = useState(true);
  const [step, setStep] = useState<"form" | "verify">("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ require_invite_code: boolean }>("/v1/auth/config")
      .then((cfg) => setRequireInvite(cfg.require_invite_code))
      .catch(() => undefined);
  }, []);

  async function onRegister(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api<{ email: string; dev_code?: string | null }>("/v1/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, invite_code: invite, locale }),
      });
      setEmail(res.email);
      if (res.dev_code) toastOk(tt("devCode", { code: res.dev_code }), 10000);
      setStep("verify");
    } catch (err) {
      toastError(err instanceof Error ? err.message : tt("registerFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/v1/auth/verify-email", { method: "POST", body: JSON.stringify({ email, code }) });
      startPageProgress();
      router.push("/dashboard");
    } catch (err) {
      toastError(err instanceof Error ? err.message : tt("verifyFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <h1 className="text-4xl font-bold text-white">{tt("register")}</h1>
      <p className="mt-2 text-sm text-gray-400">{tt("registerHint")}</p>
      <Panel className="mt-6 rounded-lg border-slate-700 bg-slate-800 p-8 shadow-2xl">
        <form onSubmit={step === "form" ? onRegister : onVerify} className="space-y-4">
          {step === "form" ? (
            <>
              <Input label={tt("email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Input label={tt("password")} type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required />
              {requireInvite && (
                <Input
                  label={tt("invite")}
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                  required
                />
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-mute">{tt("codeSent", { email })}</p>
              <Input label={tt("code")} value={code} onChange={(e) => setCode(e.target.value)} required />
            </>
          )}
          <Button className="w-full" disabled={busy}>
            {step === "form" ? tt("sendCode") : tt("finishVerify")}
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-mute">
          {tt("hasAccount")}{" "}
          <Link href="/login" className="link-accent">
            {tt("login")}
          </Link>
        </p>
      </Panel>
    </div>
  );
}
